<template>
  <div class="data-page">
    <div class="ambient-light"></div>

    <div class="container">
      <section class="hero v-reveal">
        <div class="brand-badge">SINCE 2026 / INSIGHTS</div>
        <h1 class="main-title">数据印记<span class="dot">.</span></h1>
        <p class="desc">
          每一组数字，都是社区成长的真实足迹。<br />
          冰冷的数据背后，是一群热爱生活、专注技术的我们。
        </p>
        <div class="scroll-hint">SCROLL TO EXPLORE</div>
      </section>

      <section class="stats-section">
        <div class="stats-grid">
          <div v-for="(item, index) in statsData" :key="index" class="stat-card"
            :style="{ transitionDelay: `${index * 0.1}s` }">
            <div class="card-header">
              <span class="label">{{ item.label }}</span>
              <span class="trend-tag" v-if="item.trend">↑ {{ item.trend }}%</span>
            </div>
            <div class="value">
              <span class="num">{{ item.currentValue }}</span>
              <span class="unit">{{ item.unit }}</span>
            </div>
            <div class="visual-indicator">
              <div class="bar-bg">
                <div class="bar-fill" :style="{ width: item.percent + '%' }"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="ratio-section v-reveal">
        <div class="ratio-content">
          <div class="ratio-text">
            <h2 class="section-title">优质内容生态</h2>
            <p>我们不追求流量与数量，更看重真诚与质量。来秋坚持做有价值、有温度的内容社区。</p>
            <div class="ratio-stats">
              <div class="r-item">
                <span class="r-num">86%</span>
                <span class="r-lab">内容完读率</span>
              </div>
              <div class="r-item">
                <span class="r-num">52%</span>
                <span class="r-lab">用户互动率</span>
              </div>
            </div>
          </div>
          <div class="ratio-visual">
            <div class="circle-box">
              <svg viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="45" class="bg-circle"></circle>
                <circle cx="50" cy="50" r="45" class="fg-circle"></circle>
              </svg>
              <div class="center-text">Active<br />92.7%</div>
            </div>
          </div>
        </div>
      </section>

      <section class="trend v-reveal">
        <div class="section-header">
          <h2 class="section-title">社区成长</h2>
          <span class="year-marker">Chronology</span>
        </div>
        <div class="timeline">
          <div class="line"></div>
          <div class="item" v-for="(item, i) in timeline" :key="i">
            <div class="dot-wrapper">
              <div class="dot"></div>
              <div class="dot-pulse"></div>
            </div>
            <div class="text">
              <h3 class="year">{{ item.year }}</h3>
              <p class="event">{{ item.desc }}</p>
            </div>
          </div>
        </div>
      </section>

      <section class="global v-reveal">
        <div class="map-container">
          <div class="node-grid">
            <div v-for="n in 24" :key="n" class="node-cell">
              <div class="node-point" :class="{ 'active': [2, 5, 12, 18, 22].includes(n) }"></div>
            </div>
          </div>
          <div class="map-info">
            <h2 class="section-title">国内用户覆盖</h2>
            <p>覆盖全国多省市，热爱不分地域，交流不分远近。每一位用户，都是来秋最珍贵的一部分。</p>
          </div>
        </div>
      </section>

      <section class="end v-reveal">
        <div class="line-sep"></div>
        <p class="final-text">DATA RECORDS GROWTH, PASSION CONNECTS US.</p>
        <div class="copyright">© 2026 来秋社区 LaiQiu Network</div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'

const statsData = ref([
  { label: '注册用户', value: 3600, currentValue: 0, unit: '+', percent: 78, trend: 11 },
  { label: '技术文章', value: 86, currentValue: 0, unit: '+', percent: 65, trend: 7 },
  { label: '月互动', value: 24, currentValue: 0, unit: 'k', percent: 84, trend: 13 },
  { label: '认证创作者', value: 126, currentValue: 0, unit: '人', percent: 52, trend: 4 },
  { label: '月度活跃', value: 92.7, currentValue: 0, unit: '%', percent: 93, trend: 2 },
  { label: '覆盖省市', value: 23, currentValue: 0, unit: '', percent: 48, trend: 0 },
])

const timeline = ref([
  { year: '2026', desc: '来秋社区正式成立，面向技术与生活爱好者开放' },
  { year: '2026', desc: '完成平台备案与生态搭建，核心功能全面上线' },
  { year: 'FUTURE', desc: '持续成长，与每一位认真生活的你，共赴远方' },
])

const animateNumbers = () => {
  statsData.value.forEach(item => {
    let start = 0
    const end = item.value
    const duration = 2000
    const frame = 30
    const increment = end / (duration / frame)

    const timer = setInterval(() => {
      start += increment
      if (start >= end) {
        item.currentValue = end % 1 === 0 ? end : end.toFixed(1)
        clearInterval(timer)
      } else {
        item.currentValue = end % 1 === 0 ? Math.floor(start) : start.toFixed(1)
      }
    }, frame)
  })
}

onMounted(() => {
  const ob = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('active')
        if (entry.target.classList.contains('stats-grid')) animateNumbers()
      }
    });
  }, { threshold: 0.1 });

  document.querySelectorAll('.v-reveal, .stats-grid').forEach(el => ob.observe(el))
})
</script>

<style scoped lang="scss">
$black: #000000;
$card-bg: #0a0a0a;
$border-color: rgba(255, 255, 255, 0.08);
$gold: #c9a063;
$text-dim: #666;
$text-mid: #999;

.data-page {
  background: $black;
  color: #fff;
  min-height: 100vh;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  overflow: visible;
}

.ambient-light {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background:
    radial-gradient(circle at 10% 10%, rgba(201, 160, 99, 0.05), transparent 40%),
    radial-gradient(circle at 90% 80%, rgba(255, 255, 255, 0.02), transparent 40%);
  pointer-events: none;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 60px;
}

// 通用揭示动效
.v-reveal {
  opacity: 0;
  transform: translateY(40px);
  transition: all 1.2s cubic-bezier(0.16, 1, 0.3, 1);

  &.active {
    opacity: 1;
    transform: translateY(0);
  }
}

// Hero
.hero {
  height: 90vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;

  .brand-badge {
    font-size: 11px;
    letter-spacing: 4px;
    color: $gold;
    margin-bottom: 24px;
    border: 1px solid rgba($gold, 0.3);
    padding: 6px 16px;
    border-radius: 100px;
  }

  .main-title {
    font-size: clamp(3rem, 8vw, 6rem);
    font-weight: 700;
    margin-bottom: 30px;
    letter-spacing: -2px;

    .dot {
      color: $gold;
    }
  }

  .desc {
    font-size: 18px;
    color: $text-mid;
    max-width: 600px;
    line-height: 1.8;
    font-weight: 300;
  }

  .scroll-hint {
    position: absolute;
    bottom: 50px;
    font-size: 10px;
    letter-spacing: 3px;
    color: $text-dim;
    animation: bounce 2s infinite;
  }
}

// Stats Matrix
.stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1px;
  background: $border-color;
  border: 1px solid $border-color;
  margin-bottom: 150px;
  opacity: 0;

  &.active {
    opacity: 1;
  }
}

.stat-card {
  background: $black;
  padding: 80px 50px;
  transition: 0.5s;

  &:hover {
    background: #050505;

    .bar-fill {
      background: $gold;
      box-shadow: 0 0 15px rgba($gold, 0.4);
    }
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 20px;

    .label {
      color: $text-dim;
      font-size: 14px;
      letter-spacing: 1px;
    }

    .trend-tag {
      color: $gold;
      font-size: 12px;
      font-weight: 600;
    }
  }

  .value {
    margin-bottom: 40px;

    .num {
      font-size: 60px;
      font-weight: 600;
      font-variant-numeric: tabular-nums;
    }

    .unit {
      font-size: 18px;
      color: $text-dim;
      margin-left: 4px;
    }
  }

  .bar-bg {
    width: 100%;
    height: 1px;
    background: rgba(255, 255, 255, 0.05);

    .bar-fill {
      height: 100%;
      background: rgba(255, 255, 255, 0.2);
      transition: width 2s cubic-bezier(0.16, 1, 0.3, 1);
    }
  }
}

// Ratio Section
.ratio-section {
  padding: 100px 0 200px;

  .ratio-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 80px;
  }

  .ratio-text {
    flex: 1;

    .section-title {
      font-size: 32px;
      margin-bottom: 30px;
    }

    p {
      color: $text-mid;
      font-size: 18px;
      line-height: 1.8;
      margin-bottom: 40px;
    }
  }

  .ratio-stats {
    display: flex;
    gap: 40px;

    .r-num {
      font-size: 32px;
      font-weight: 600;
      display: block;
      color: $gold;
    }

    .r-lab {
      font-size: 14px;
      color: $text-dim;
    }
  }
}

.circle-box {
  position: relative;
  width: 280px;
  height: 280px;

  svg {
    transform: rotate(-90deg);
  }

  .bg-circle {
    fill: none;
    stroke: rgba(255, 255, 255, 0.03);
    stroke-width: 4;
  }

  .fg-circle {
    fill: none;
    stroke: $gold;
    stroke-width: 4;
    stroke-dasharray: 283;
    stroke-dashoffset: 40; // 模拟98.4%
    stroke-linecap: round;
  }

  .center-text {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
    color: $text-mid;
    font-size: 14px;
    line-height: 1.4;
  }
}

// Timeline
.trend {
  margin-bottom: 200px;

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-bottom: 80px;

    .section-title {
      font-size: 32px;
      margin: 0;
    }

    .year-marker {
      color: $text-dim;
      letter-spacing: 4px;
      font-size: 12px;
    }
  }
}

.timeline {
  position: relative;

  .line {
    position: absolute;
    left: 7px;
    top: 0;
    width: 1px;
    height: 100%;
    background: linear-gradient(to bottom, $gold, transparent);
  }

  .item {
    padding-left: 60px;
    margin-bottom: 80px;
    position: relative;

    .dot-wrapper {
      position: absolute;
      left: 0;
      top: 8px;

      .dot {
        width: 15px;
        height: 15px;
        background: $black;
        border: 2px solid $gold;
        border-radius: 50%;
        z-index: 2;
        position: relative;
      }

      .dot-pulse {
        position: absolute;
        top: 0;
        left: 0;
        width: 15px;
        height: 15px;
        background: $gold;
        border-radius: 50%;
        opacity: 0.4;
        animation: pulse-ring 2s infinite;
      }
    }

    .year {
      font-size: 28px;
      margin-bottom: 10px;
      font-weight: 600;
    }

    .event {
      color: $text-mid;
      font-size: 16px;
    }
  }
}

// Global Node Map
.global {
  margin-bottom: 200px;

  .map-container {
    display: flex;
    align-items: center;
    gap: 60px;
    background: rgba(255, 255, 255, 0.02);
    padding: 60px;
    border-radius: 40px;
    border: 1px solid $border-color;
  }

  .node-grid {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 20px;

    .node-cell {
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .node-point {
      width: 4px;
      height: 4px;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 50%;

      &.active {
        background: $gold;
        box-shadow: 0 0 10px $gold;
        animation: glow 2s infinite alternate;
      }
    }
  }

  .map-info {
    flex: 1;

    .section-title {
      font-size: 28px;
      margin-bottom: 20px;
    }

    p {
      color: $text-mid;
      line-height: 1.8;
    }
  }
}

// End
.end {
  text-align: center;
  padding-bottom: 100px;

  .line-sep {
    width: 1px;
    height: 80px;
    background: linear-gradient($gold, transparent);
    margin: 0 auto 40px;
  }

  .final-text {
    font-size: 14px;
    letter-spacing: 5px;
    color: $text-dim;
  }

  .copyright {
    margin-top: 30px;
    font-size: 10px;
    color: #333;
  }
}

@keyframes bounce {

  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0);
  }

  40% {
    transform: translateY(-10px);
  }

  60% {
    transform: translateY(-5px);
  }
}

@keyframes pulse-ring {
  0% {
    transform: scale(0.8);
    opacity: 0.5;
  }

  100% {
    transform: scale(2.5);
    opacity: 0;
  }
}

@keyframes glow {
  from {
    opacity: 0.4;
  }

  to {
    opacity: 1;
  }
}

@media (max-width: 900px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .ratio-content,
  .global .map-container {
    flex-direction: column;
    text-align: center;
  }

  .container {
    padding: 0 30px;
  }
}
</style>